import { Component, OnInit, signal, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'
import { finalize } from 'rxjs/operators'

import { BaseComponent, FeatureType, ListItem, VpJob } from '../../shared'
import { CathodicComponent } from './cathodic/cathodic.component'
import { ConcreteComponent } from './concrete/concrete.component'
import { ConcreteResultComponent } from './concrete-result/concrete-result.component'
import { DirtComponent } from './dirt/dirt.component'
import { MechanicalComponent } from './mechanical/mechanical.component'
import { PaintingComponent } from './painting/painting.component'
import { PipeComponent } from './pipe/pipe.component'
import { RoadComponent } from './road/road.component'
import { SoilResultComponent } from './soil-result/soil-result.component'

@Component({
    selector: 'app-quality-control',
    templateUrl: './quality-control.component.html',
    styleUrls: ['./quality-control.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        NgbNavOutlet,
        ConcreteComponent,
        DirtComponent,
        PipeComponent,
        RoadComponent,
        MechanicalComponent,
        PaintingComponent,
        CathodicComponent,
        ConcreteResultComponent,
        SoilResultComponent,
    ],
})
export class QualityControlComponent extends BaseComponent implements OnInit {
    @ViewChild('concreteComponent') concreteComponent!: ConcreteComponent
    @ViewChild('dirtComponent') dirtComponent!: DirtComponent
    @ViewChild('pipeComponent') pipeComponent!: PipeComponent
    @ViewChild('roadComponent') roadComponent!: RoadComponent
    @ViewChild('mechanicalComponent') mechanicalComponent!: MechanicalComponent
    @ViewChild('paintingComponent') paintingComponent!: PaintingComponent
    @ViewChild('cathodicComponent') cathodicComponent!: CathodicComponent
    @ViewChild('concreteResultComponent') concreteResultComponent!: ConcreteResultComponent
    @ViewChild('soilResultComponent') soilResultComponent!: SoilResultComponent

    selectedJob = signal<string>('')
    jobMap: ListItem<string>[] = []
    allJobs: VpJob[] = []
    active = 1

    // Tab categories
    readonly categories = [
        { id: 1, name: 'Concrete' },
        { id: 2, name: 'Dirt' },
        { id: 3, name: 'Pipe' },
        { id: 4, name: 'Road' },
        { id: 5, name: 'Mechanical' },
        { id: 6, name: 'Painting' },
        { id: 7, name: 'Cathodic' },
        { id: 8, name: 'Concrete Test Results' },
        { id: 9, name: 'Soil / Density Results' },
    ]

    constructor() {
        super()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    get selectedJobName(): string {
        const jobId = this.selectedJob()
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id} ${job.name}` : jobId
    }

    ngOnInit() {
        this.loadJobs()
    }

    loadJobs() {
        this.cancelableRequest(
            'loadJobs',
            true,
            this.services.data
                .load(true, true, this.services.data.vpJobs.queryAll())
                .pipe(finalize(() => this.initialLoadingComplete()))
                .subscribe({
                    next: (result: unknown[]) => {
                        this.allJobs = result[0] as VpJob[]
                        this.buildJobMap()
                    },
                    error: () => {
                        this.services.notification.error('Failed to load projects')
                    },
                }),
        )
    }

    buildJobMap() {
        const jobMap: ListItem<string>[] = []
        jobMap.push(new ListItem<string>('', 'Select a Project'))

        // Add active projects
        this.allJobs.forEach(job => {
            jobMap.push(new ListItem<string>(job.id, job.id + ' ' + job.name))
        })

        this.jobMap = jobMap
    }

    onJobChanged() {
        const jobId = this.selectedJob()
        if (this.concreteComponent) {
            this.concreteComponent.loadConcreteData(jobId)
        }
        if (this.dirtComponent) {
            this.dirtComponent.loadDirtData(jobId)
        }
        if (this.pipeComponent) {
            this.pipeComponent.loadPipeData(jobId)
        }
        if (this.roadComponent) {
            this.roadComponent.loadRoadData(jobId)
        }
        if (this.mechanicalComponent) {
            this.mechanicalComponent.loadMechanicalData(jobId)
        }
        if (this.paintingComponent) {
            this.paintingComponent.loadPaintingData(jobId)
        }
        if (this.cathodicComponent) {
            this.cathodicComponent.loadCathodicData(jobId)
        }
        if (this.concreteResultComponent) {
            this.concreteResultComponent.loadConcreteResultData(jobId)
        }
        if (this.soilResultComponent) {
            this.soilResultComponent.loadSoilResultData(jobId)
        }
    }

    tabChanged() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            if (this.active === 1 && this.concreteComponent) {
                this.concreteComponent.loadConcreteData(jobId)
            }
            if (this.active === 2 && this.dirtComponent) {
                this.dirtComponent.loadDirtData(jobId)
            }
            if (this.active === 3 && this.pipeComponent) {
                this.pipeComponent.loadPipeData(jobId)
            }
            if (this.active === 4 && this.roadComponent) {
                this.roadComponent.loadRoadData(jobId)
            }
            if (this.active === 5 && this.mechanicalComponent) {
                this.mechanicalComponent.loadMechanicalData(jobId)
            }
            if (this.active === 6 && this.paintingComponent) {
                this.paintingComponent.loadPaintingData(jobId)
            }
            if (this.active === 7 && this.cathodicComponent) {
                this.cathodicComponent.loadCathodicData(jobId)
            }
            if (this.active === 8 && this.concreteResultComponent) {
                this.concreteResultComponent.loadConcreteResultData(jobId)
            }
            if (this.active === 9 && this.soilResultComponent) {
                this.soilResultComponent.loadSoilResultData(jobId)
            }
        }
    }
}
